<template>
  <div class="blogs-wrapper">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1>Blogs</h1>
        <p>Manage blog articles, content and publishing information.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add Blog
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-news"></i>
        </div>
        <div>
          <span>Total Blogs</span>
          <strong>{{ blogs.length }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-trending-up"></i>
        </div>
        <div>
          <span>Trending</span>
          <strong>{{ trendingBlogs }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-category"></i>
        </div>
        <div>
          <span>Categories</span>
          <strong>{{ categoryCount }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-edit"></i>
        </div>
        <div>
          <span>This Month</span>
          <strong>{{ thisMonthBlogs }}</strong>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <div class="content-card">
      <!-- Toolbar -->
      <div class="toolbar">
        <div class="search-box">
          <i class="bx bx-search"></i>

          <input
            v-model="search"
            type="text"
            placeholder="Search blogs, keywords or creator..."
          />
        </div>

        <select v-model="categoryFilter" class="filter-select">
          <option value="">All Categories</option>

          <option
            v-for="category in categories"
            :key="category.cat_id"
            :value="category.cat_id"
          >
            {{ category.cat_name }}
          </option>
        </select>

        <select v-model="difficultyFilter" class="filter-select">
          <option value="">All Levels</option>
          <option value="Beginner">Beginner</option>
          <option value="Intermediate">Intermediate</option>
          <option value="Advanced">Advanced</option>
        </select>

        <select v-model="platformFilter" class="filter-select">
          <option value="">All Platforms</option>
          <option value="Website">Website</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="Medium">Medium</option>
          <option value="Other">Other</option>
        </select>

        <select v-model="trendingFilter" class="filter-select">
          <option value="">Trending: All</option>
          <option value="true">Trending</option>
          <option value="false">Not Trending</option>
        </select>

        <button class="reset-btn" @click="resetFilters">
          <i class="bx bx-reset"></i>
          Reset
        </button>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Blog</th>
              <th>Category</th>
              <th>Difficulty</th>
              <th>Creator</th>
              <th>Platform</th>
              <th>Duration</th>
              <th>Trending</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="paginatedBlogs.length === 0">
              <td colspan="9" class="empty-state">
                <i class="bx bx-news"></i>
                <h3>No blogs found</h3>
                <p>Try changing your search or filters.</p>
              </td>
            </tr>

            <tr
              v-for="blog in paginatedBlogs"
              :key="blog.blog_id"
            >
              <!-- Blog -->
              <td>
                <div class="blog-info">
                  <div class="blog-image">
                    <img
                      v-if="blog.blog_image"
                      :src="blog.blog_image"
                      :alt="blog.blog_title"
                    />

                    <i
                      v-else
                      class="bx bx-news"
                    ></i>
                  </div>

                  <div class="blog-details">
                    <strong>
                      {{ blog.blog_title }}
                    </strong>

                    <small>
                      #{{ blog.blog_id }}

                      <span v-if="blog.blog_keyword">
                        · {{ blog.blog_keyword }}
                      </span>
                    </small>
                  </div>
                </div>
              </td>

              <!-- Category -->
              <td>
                <span class="category-badge">
                  {{ blog.category_name || "—" }}
                </span>
              </td>

              <!-- Difficulty -->
              <td>
                <span
                  class="difficulty-badge"
                  :class="difficultyClass(blog.blog_diff_level)"
                >
                  {{ blog.blog_diff_level || "—" }}
                </span>
              </td>

              <!-- Creator -->
              <td>
                <div class="creator-info">
                  <strong>
                    {{ blog.creator_name || "—" }}
                  </strong>

                  <small v-if="blog.blog_creator">
                    Creator #{{ blog.blog_creator }}
                  </small>
                </div>
              </td>

              <!-- Platform -->
              <td>
                <span class="platform-badge">
                  {{ blog.blog_platform || "—" }}
                </span>
              </td>

              <!-- Duration -->
              <td>
                <span class="duration">
                  {{
                    blog.blog_duration
                      ? blog.blog_duration + " min"
                      : "—"
                  }}
                </span>
              </td>

              <!-- Trending -->
              <td>
                <span
                  class="trending-badge"
                  :class="{
                    active: blog.blog_is_trending
                  }"
                >
                  <i
                    class="bx"
                    :class="
                      blog.blog_is_trending
                        ? 'bx-trending-up'
                        : 'bx-minus'
                    "
                  ></i>

                  {{
                    blog.blog_is_trending
                      ? "Trending"
                      : "Normal"
                  }}
                </span>
              </td>

              <!-- Date -->
              <td>
                <span class="date-text">
                  {{ formatDate(blog.blog_datetime) }}
                </span>
              </td>

              <!-- Actions -->
              <td>
                <div class="actions">
                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewBlog(blog)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editBlog(blog)"
                  >
                    <i class="bx bx-edit-alt"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(blog)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="totalPages > 1">
        <span>
          Showing {{ startItem }}–{{ endItem }}
          of {{ filteredBlogs.length }}
        </span>

        <div class="pagination-buttons">
          <button
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          >
            {{ page }}
          </button>

          <button
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            <i class="bx bx-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >
      <div class="modal">
        <div class="modal-header">
          <div>
            <h2>
              {{ isEditing ? "Edit Blog" : "Add Blog" }}
            </h2>

            <p>
              {{
                isEditing
                  ? "Update blog article information."
                  : "Create a new blog article."
              }}
            </p>
          </div>

          <button
            class="close-btn"
            @click="closeFormModal"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <!-- Basic -->
            <div class="form-group full">
              <label>Blog Title <span>*</span></label>

              <input
                v-model="form.blog_title"
                type="text"
                placeholder="Enter blog title"
              />
            </div>

            <div class="form-group">
              <label>Category</label>

              <select v-model.number="form.blog_cat">
                <option :value="null">
                  Select Category
                </option>

                <option
                  v-for="category in categories"
                  :key="category.cat_id"
                  :value="category.cat_id"
                >
                  {{ category.cat_name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Difficulty Level</label>

              <select v-model="form.blog_diff_level">
                <option value="">
                  Select Difficulty
                </option>
                <option value="Beginner">
                  Beginner
                </option>
                <option value="Intermediate">
                  Intermediate
                </option>
                <option value="Advanced">
                  Advanced
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Duration</label>

              <input
                v-model.number="form.blog_duration"
                type="number"
                min="0"
                step="0.01"
                placeholder="Minutes"
              />
            </div>

            <div class="form-group">
              <label>Creator</label>

              <select v-model.number="form.blog_creator">
                <option :value="null">
                  Select Creator
                </option>

                <option
                  v-for="creator in creators"
                  :key="creator.creator_id"
                  :value="creator.creator_id"
                >
                  {{ creator.cre_username }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Platform</label>

              <select v-model="form.blog_platform">
                <option value="">
                  Select Platform
                </option>
                <option value="Website">
                  Website
                </option>
                <option value="LinkedIn">
                  LinkedIn
                </option>
                <option value="Medium">
                  Medium
                </option>
                <option value="Other">
                  Other
                </option>
              </select>
            </div>

            <!-- Description -->
            <div class="form-group full">
              <label>Blog Description</label>

              <textarea
                v-model="form.blog_desc"
                rows="3"
                placeholder="Short blog description..."
              ></textarea>
            </div>

            <div class="form-group full">
              <label>Blog Content</label>

              <textarea
                v-model="form.blog_content"
                rows="8"
                placeholder="Enter complete blog content..."
              ></textarea>
            </div>

            <!-- Image -->
            <div class="form-group full">
              <label>Blog Image URL</label>

              <input
                v-model="form.blog_image"
                type="text"
                placeholder="https://example.com/blog-image.jpg"
              />
            </div>

            <!-- SEO -->
            <div class="form-group full">
              <label>Meta Title</label>

              <input
                v-model="form.blog_meta_title"
                type="text"
                placeholder="SEO meta title"
              />
            </div>

            <div class="form-group full">
              <label>Keywords</label>

              <textarea
                v-model="form.blog_keyword"
                rows="2"
                placeholder="ai, creator, technology..."
              ></textarea>
            </div>

            <!-- Trending -->
            <div class="form-group full">
              <label class="checkbox-label">
                <input
                  v-model="form.blog_is_trending"
                  type="checkbox"
                />

                <span>
                  Mark this blog as Trending
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="closeFormModal"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            @click="saveBlog"
          >
            <i class="bx bx-check"></i>

            {{
              isEditing
                ? "Update Blog"
                : "Create Blog"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="showViewModal = false"
    >
      <div class="modal view-modal">
        <div class="modal-header">
          <div>
            <h2>Blog Details</h2>
            <p>Complete blog information.</p>
          </div>

          <button
            class="close-btn"
            @click="showViewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div
          class="modal-body"
          v-if="selectedBlog"
        >
          <!-- Blog Header -->
          <div class="blog-profile">
            <div class="large-blog-image">
              <img
                v-if="selectedBlog.blog_image"
                :src="selectedBlog.blog_image"
                :alt="selectedBlog.blog_title"
              />

              <i
                v-else
                class="bx bx-news"
              ></i>
            </div>

            <div>
              <h3>
                {{ selectedBlog.blog_title }}
              </h3>

              <p>
                Blog #{{ selectedBlog.blog_id }}
              </p>

              <div class="blog-badges">
                <span class="category-badge">
                  {{ selectedBlog.category_name || "—" }}
                </span>

                <span
                  class="difficulty-badge"
                  :class="
                    difficultyClass(
                      selectedBlog.blog_diff_level
                    )
                  "
                >
                  {{
                    selectedBlog.blog_diff_level ||
                    "—"
                  }}
                </span>

                <span
                  v-if="selectedBlog.blog_is_trending"
                  class="trending-badge active"
                >
                  <i class="bx bx-trending-up"></i>
                  Trending
                </span>
              </div>
            </div>
          </div>

          <!-- Blog Information -->
          <div class="detail-section">
            <h4>Blog Information</h4>

            <div class="details-grid">
              <div class="detail-item">
                <span>Creator</span>

                <strong>
                  {{
                    selectedBlog.creator_name ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Platform</span>

                <strong>
                  {{
                    selectedBlog.blog_platform ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Duration</span>

                <strong>
                  {{
                    selectedBlog.blog_duration
                      ? selectedBlog.blog_duration +
                        " min"
                      : "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Category ID</span>

                <strong>
                  {{ selectedBlog.blog_cat || "—" }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Creator ID</span>

                <strong>
                  {{
                    selectedBlog.blog_creator || "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Created</span>

                <strong>
                  {{
                    formatDate(
                      selectedBlog.blog_datetime
                    )
                  }}
                </strong>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="detail-section">
            <h4>Description</h4>

            <div class="content-box">
              <p>
                {{
                  selectedBlog.blog_desc ||
                  "No description available."
                }}
              </p>
            </div>
          </div>

          <!-- Content -->
          <div class="detail-section">
            <h4>Blog Content</h4>

            <div class="content-box blog-content">
              <p>
                {{
                  selectedBlog.blog_content ||
                  "No blog content available."
                }}
              </p>
            </div>
          </div>

          <!-- SEO -->
          <div class="detail-section">
            <h4>SEO Information</h4>

            <div class="details-grid">
              <div class="detail-item">
                <span>Meta Title</span>

                <strong>
                  {{
                    selectedBlog.blog_meta_title ||
                    "—"
                  }}
                </strong>
              </div>

              <div class="detail-item">
                <span>Keywords</span>

                <strong>
                  {{
                    selectedBlog.blog_keyword ||
                    "—"
                  }}
                </strong>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="secondary-btn"
            @click="showViewModal = false"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="
              showViewModal = false;
              editBlog(selectedBlog);
            "
          >
            <i class="bx bx-edit-alt"></i>
            Edit Blog
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="showDeleteModal = false"
    >
      <div class="delete-modal">
        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h2>Delete Blog?</h2>

        <p>
          Are you sure you want to delete
          <strong>
            {{ selectedBlog?.blog_title }}
          </strong>
          ? This action cannot be undone.
        </p>

        <div class="delete-actions">
          <button
            class="secondary-btn"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            @click="deleteBlog"
          >
            Delete Blog
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getBlogsApi,
  getBlogStatsApi,
  getBlogApi,
  createBlogApi,
  updateBlogApi,
  deleteBlogApi,
} from "@/apis/blogapis";

import { getCategoriesApi } from "@/apis/categoryapis";
import { getCreatorsApi } from "@/apis/creatorapis";

export default {
  name: "BlogsScreen",

  data() {
    return {
      // =====================================================
      // FILTERS
      // =====================================================

      search: "",
      categoryFilter: "",
      difficultyFilter: "",
      platformFilter: "",
      trendingFilter: "",
      creators: [],

      // =====================================================
      // PAGINATION
      // =====================================================

      currentPage: 1,
      perPage: 8,
      totalBlogs: 0,
      totalPagesCount: 1,

      // =====================================================
      // DATA
      // =====================================================

      blogs: [],

      // =====================================================
      // FILTER OPTIONS
      // =====================================================

      categories: [],

      // =====================================================
      // STATS
      // =====================================================

      stats: {
        totalBlogs: 0,
        trendingBlogs: 0,
        categoryCount: 0,
        thisMonthBlogs: 0,
      },

      // =====================================================
      // LOADING
      // =====================================================

      loading: false,
      saving: false,
      deleting: false,

      // =====================================================
      // MODALS
      // =====================================================

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,

      selectedBlog: null,

      // =====================================================
      // FORM
      // =====================================================

      form: {
        blog_id: null,
        blog_title: "",
        blog_meta_title: "",
        blog_keyword: "",
        blog_cat: null,
        blog_diff_level: "",
        blog_desc: "",
        blog_content: "",
        blog_duration: null,
        blog_creator: null,
        blog_image: "",
        blog_platform: "",
        blog_is_trending: false,
      },
    };
  },

  computed: {
    // =====================================================
    // API DATA ALREADY PAGINATED
    // =====================================================

    paginatedBlogs() {
      return this.blogs;
    },

    filteredBlogs() {
      return this.blogs;
    },

    totalPages() {
      return this.totalPagesCount;
    },

    // =====================================================
    // PAGINATION INFO
    // =====================================================

    startItem() {
      if (!this.totalBlogs) {
        return 0;
      }

      return (
        (this.currentPage - 1) *
          this.perPage +
        1
      );
    },

    endItem() {
      return Math.min(
        this.currentPage * this.perPage,
        this.totalBlogs
      );
    },

    // =====================================================
    // STATS
    // =====================================================

    trendingBlogs() {
      return Number(
        this.stats.trendingBlogs || 0
      );
    },

    categoryCount() {
      return Number(
        this.stats.categoryCount || 0
      );
    },

    thisMonthBlogs() {
      return Number(
        this.stats.thisMonthBlogs || 0
      );
    },
  },

  watch: {
    // =====================================================
    // SEARCH
    // =====================================================

    search() {
      this.currentPage = 1;
      this.loadBlogs();
    },

    // =====================================================
    // CATEGORY
    // =====================================================

    categoryFilter() {
      this.currentPage = 1;
      this.loadBlogs();
    },

    // =====================================================
    // DIFFICULTY
    // =====================================================

    difficultyFilter() {
      this.currentPage = 1;
      this.loadBlogs();
    },

    // =====================================================
    // PLATFORM
    // =====================================================

    platformFilter() {
      this.currentPage = 1;
      this.loadBlogs();
    },

    // =====================================================
    // TRENDING
    // =====================================================

    trendingFilter() {
      this.currentPage = 1;
      this.loadBlogs();
    },
  },

  mounted() {
    this.loadBlogs();
    this.loadStats();
    this.loadCreators();
    this.loadCategories();
  },

  methods: {
    // =====================================================
    // LOAD BLOGS
    // =====================================================
    async loadCreators() {
      try {
        const res = await getCreatorsApi({
          page: 1,
          limit: 100,
          search: "",
        });

        if (res && res.status !== false) {
          this.creators = Array.isArray(res.creators)
            ? res.creators
            : [];
        } else {
          this.creators = [];
        }
      } catch (error) {
        console.error(
          "Load Blog Creators Error:",
          error
        );

        this.creators = [];
      }
    },
    async loadBlogs() {
      this.loading = true;

      try {
        const res = await getBlogsApi({
          page: this.currentPage,
          limit: this.perPage,
          search: this.search,
          category: this.categoryFilter,
          difficulty: this.difficultyFilter,
          platform: this.platformFilter,
          trending: this.trendingFilter,
        });

        if (
          res &&
          res.status !== false
        ) {
          this.blogs =
            res.blogs || [];

          this.totalBlogs = Number(
            res.total || 0
          );

          this.totalPagesCount = Math.max(
            Number(res.totalPages || 1),
            1
          );

          this.currentPage = Number(
            res.page || this.currentPage
          );

          // -----------------------------------------------
          // CATEGORY OPTIONS
          // -----------------------------------------------

          if (
            Array.isArray(res.categories)
          ) {
            this.categories =
              res.categories;
          }

          // -----------------------------------------------
          // SAFETY AFTER DELETE / FILTER
          // -----------------------------------------------

          if (
            this.currentPage >
            this.totalPagesCount
          ) {
            this.currentPage =
              this.totalPagesCount;

            await this.loadBlogs();
          }
        } else {
          this.blogs = [];
          this.totalBlogs = 0;
          this.totalPagesCount = 1;
        }
      } catch (error) {
        console.error(
          "Load Blogs Error:",
          error
        );

        this.blogs = [];
        this.totalBlogs = 0;
        this.totalPagesCount = 1;
      } finally {
        this.loading = false;
      }
    },

    // =====================================================
    // LOAD CATEGORIES
    // =====================================================

    async loadCategories() {
        try {
          const res = await getCategoriesApi({
            page: 1,
            limit: 100,
            search: "",
            cat_type: "blog",
          });

          if (res && res.status !== false) {
            this.categories = res.categories || [];
          } else {
            this.categories = [];
          }
        } catch (error) {
          console.error("Load Blog Categories Error:", error);
          this.categories = [];
        }
      },

    // =====================================================
    // LOAD STATS
    // =====================================================

    async loadStats() {
      try {
        const res =
          await getBlogStatsApi();

        if (
          res &&
          res.status !== false &&
          res.stats
        ) {
          this.stats = {
            ...this.stats,
            ...res.stats,
          };
        }
      } catch (error) {
        console.error(
          "Load Blog Stats Error:",
          error
        );
      }
    },

    // =====================================================
    // RESET FILTERS
    // =====================================================

    resetFilters() {
      this.search = "";
      this.categoryFilter = "";
      this.difficultyFilter = "";
      this.platformFilter = "";
      this.trendingFilter = "";
      this.currentPage = 1;

      this.$nextTick(() => {
        this.loadBlogs();
      });
    },

    // =====================================================
    // EMPTY FORM
    // =====================================================

    getEmptyForm() {
      return {
        blog_id: null,
        blog_title: "",
        blog_meta_title: "",
        blog_keyword: "",
        blog_cat: null,
        blog_diff_level: "",
        blog_desc: "",
        blog_content: "",
        blog_duration: null,
        blog_creator: null,
        blog_image: "",
        blog_platform: "",
        blog_is_trending: false,
      };
    },

    // =====================================================
    // ADD BLOG
    // =====================================================

    openAddModal() {
      this.isEditing = false;
      this.selectedBlog = null;
      this.form = this.getEmptyForm();
      this.showFormModal = true;
    },

    // =====================================================
    // CLOSE FORM
    // =====================================================

    closeFormModal() {
      if (this.saving) {
        return;
      }

      this.showFormModal = false;
      this.isEditing = false;
      this.selectedBlog = null;
      this.form = this.getEmptyForm();
    },

    // =====================================================
    // VIEW BLOG
    // =====================================================

    async viewBlog(blog) {
      if (!blog?.blog_id) {
        return;
      }

      try {
        const res =
          await getBlogApi(
            blog.blog_id
          );

        if (
          res &&
          res.status !== false &&
          res.blog
        ) {
          this.selectedBlog =
            res.blog;
        } else {
          this.selectedBlog =
            blog;
        }

        this.showViewModal = true;
      } catch (error) {
        console.error(
          "View Blog Error:",
          error
        );

        this.selectedBlog =
          blog;

        this.showViewModal = true;
      }
    },

    // =====================================================
    // EDIT BLOG
    // =====================================================

    async editBlog(blog) {
      if (!blog?.blog_id) {
        return;
      }

      try {
        const res =
          await getBlogApi(
            blog.blog_id
          );

        const data =
          res &&
          res.status !== false &&
          res.blog
            ? res.blog
            : blog;

        this.selectedBlog =
          data;

        this.isEditing = true;

        this.form = {
          ...this.getEmptyForm(),

          blog_id:
            data.blog_id,

          blog_title:
            data.blog_title || "",

          blog_meta_title:
            data.blog_meta_title || "",

          blog_keyword:
            data.blog_keyword || "",

          blog_cat:
            data.blog_cat || null,

          blog_diff_level:
            data.blog_diff_level || "",

          blog_desc:
            data.blog_desc || "",

          blog_content:
            data.blog_content || "",

          blog_duration:
            data.blog_duration !== null &&
            data.blog_duration !== undefined
              ? Number(
                  data.blog_duration
                )
              : null,

          blog_creator:
            data.blog_creator || null,

          blog_image:
            data.blog_image || "",

          blog_platform:
            data.blog_platform || "",

          blog_is_trending:
            data.blog_is_trending === true ||
            data.blog_is_trending === 1 ||
            data.blog_is_trending === "1",
        };

        this.showViewModal = false;
        this.showFormModal = true;
      } catch (error) {
        console.error(
          "Edit Blog Error:",
          error
        );
      }
    },

    // =====================================================
    // SAVE BLOG
    // =====================================================

    async saveBlog() {
      if (
        !this.form.blog_title ||
        !this.form.blog_title.trim()
      ) {
        alert(
          "Please enter blog title."
        );

        return;
      }

      this.saving = true;

      try {
        const payload = {
          ...this.form,
          blog_cat:
            this.form.blog_cat !== null &&
            this.form.blog_cat !== ""
              ? Number(this.form.blog_cat)
              : null,
        };

        delete payload.blog_parent;
        delete payload.blog_parent_type;

        let res;

        if (this.isEditing) {
          res =
            await updateBlogApi(
              payload
            );
        } else {
          res =
            await createBlogApi(
              payload
            );
        }

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              (
                this.isEditing
                  ? "Blog updated successfully."
                  : "Blog created successfully."
              )
          );

          this.showFormModal = false;
          this.isEditing = false;
          this.selectedBlog = null;
          this.form =
            this.getEmptyForm();

          await this.loadBlogs();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Unable to save blog."
          );
        }
      } catch (error) {
        console.error(
          "Save Blog Error:",
          error
        );

        alert(
          error?.response?.data?.success ||
            "Something went wrong while saving blog."
        );
      } finally {
        this.saving = false;
      }
    },

    // =====================================================
    // CONFIRM DELETE
    // =====================================================

    confirmDelete(blog) {
      if (!blog?.blog_id) {
        return;
      }

      this.selectedBlog =
        blog;

      this.showDeleteModal = true;
    },

    // =====================================================
    // DELETE BLOG
    // =====================================================

    async deleteBlog() {
      if (
        !this.selectedBlog?.blog_id
      ) {
        return;
      }

      this.deleting = true;

      try {
        const res =
          await deleteBlogApi(
            this.selectedBlog.blog_id
          );

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              "Blog deleted successfully."
          );

          this.showDeleteModal = false;
          this.selectedBlog = null;

          // If last item on current page was deleted
          if (
            this.blogs.length === 1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadBlogs();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Blog deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Blog Error:",
          error
        );

        alert(
          error?.response?.data?.success ||
            "Something went wrong while deleting blog."
        );
      } finally {
        this.deleting = false;
      }
    },

    // =====================================================
    // PAGINATION
    // =====================================================

    goToPage(page) {
      if (
        page < 1 ||
        page > this.totalPagesCount ||
        page === this.currentPage
      ) {
        return;
      }

      this.currentPage = page;
      this.loadBlogs();
    },

    previousPage() {
      if (
        this.currentPage <= 1
      ) {
        return;
      }

      this.currentPage--;
      this.loadBlogs();
    },

    nextPage() {
      if (
        this.currentPage >=
        this.totalPagesCount
      ) {
        return;
      }

      this.currentPage++;
      this.loadBlogs();
    },

    // =====================================================
    // DIFFICULTY CSS
    // =====================================================

    difficultyClass(level) {
      if (level === "Beginner") {
        return "beginner";
      }

      if (level === "Intermediate") {
        return "intermediate";
      }

      if (level === "Advanced") {
        return "advanced";
      }

      return "";
    },

    // =====================================================
    // DATE
    // =====================================================

    formatDate(date) {
      if (!date) {
        return "—";
      }

      const value =
        new Date(date);

      if (
        Number.isNaN(
          value.getTime()
        )
      ) {
        return date;
      }

      return value.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },
  },
};
</script>

<style scoped>
.blogs-wrapper {
  padding: 24px;
  background: #f8fafc;
  min-height: calc(100vh - 70px);
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 25px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.reset-btn,
.danger-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: white;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: white;
  color: #334155;
  border-color: #e2e8f0;
}

.secondary-btn:hover {
  background: #f8fafc;
}

.reset-btn {
  background: white;
  color: #475569;
  border-color: #e2e8f0;
}

.reset-btn:hover {
  background: #f8fafc;
}

.danger-btn {
  background: #dc2626;
  color: white;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 17px;
  display: flex;
  align-items: center;
  gap: 13px;
}

.stat-icon {
  width: 43px;
  height: 43px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-card span {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.stat-card strong {
  font-size: 21px;
}

/* Card */
.content-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 360px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-select {
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  font-size: 13px;
  color: #334155;
  background: white;
}

.search-box input {
  width: 100%;
  padding: 0 13px 0 39px;
}

.filter-select {
  min-width: 135px;
  padding: 0 12px;
}

.search-box input:focus,
.filter-select:focus,
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #93c5fd;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: left;
  padding: 13px 16px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

td {
  padding: 13px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  font-size: 13px;
}

tbody tr:hover {
  background: #fafcff;
}

/* Blog */
.blog-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 270px;
}

.blog-image {
  width: 50px;
  height: 42px;
  border-radius: 7px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 18px;
}

.blog-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-details strong {
  display: block;
  max-width: 240px;
  font-size: 12px;
  line-height: 1.4;
}

.blog-details small {
  display: block;
  color: #94a3b8;
  font-size: 10px;
  margin-top: 3px;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-badge,
.platform-badge,
.difficulty-badge,
.trending-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 9px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.category-badge {
  background: #eff6ff;
  color: #2563eb;
}

.platform-badge {
  background: #f1f5f9;
  color: #475569;
}

.difficulty-badge.beginner {
  background: #f0fdf4;
  color: #15803d;
}

.difficulty-badge.intermediate {
  background: #fff7ed;
  color: #c2410c;
}

.difficulty-badge.advanced {
  background: #fef2f2;
  color: #dc2626;
}

.creator-info strong {
  display: block;
  font-size: 12px;
}

.creator-info small {
  display: block;
  color: #94a3b8;
  font-size: 10px;
  margin-top: 3px;
}

.duration {
  color: #475569;
  font-size: 12px;
  white-space: nowrap;
}

.trending-badge {
  background: #f1f5f9;
  color: #64748b;
}

.trending-badge.active {
  background: #f0fdf4;
  color: #15803d;
}

.date-text {
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
}

/* Actions */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}

.icon-btn.view {
  color: #64748b;
}

.icon-btn.view:hover {
  background: #f1f5f9;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.edit:hover {
  background: #eff6ff;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn.delete:hover {
  background: #fef2f2;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px !important;
  color: #94a3b8;
}

.empty-state i {
  font-size: 42px;
}

.empty-state h3 {
  margin: 10px 0 4px;
  color: #334155;
  font-size: 15px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 31px;
  height: 31px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: #475569;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: white;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 780px;
  max-width: 100%;
  max-height: 90vh;
  background: white;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.view-modal {
  width: 720px;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-header h2 {
  margin: 0 0 4px;
  font-size: 19px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: #f8fafc;
  border-radius: 7px;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}

/* Form */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 11px;
  outline: none;
  font-size: 13px;
  color: #334155;
  background: white;
  font-family: inherit;
}

.form-group input,
.form-group select {
  height: 40px;
}

.form-group textarea {
  resize: vertical;
  line-height: 1.5;
}

.checkbox-label {
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input {
  width: 16px;
  height: 16px;
}

/* View */
.blog-profile {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.large-blog-image {
  width: 90px;
  height: 65px;
  flex-shrink: 0;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 25px;
}

.large-blog-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-profile h3 {
  margin: 0 0 3px;
  font-size: 18px;
  line-height: 1.4;
}

.blog-profile p {
  margin: 0 0 8px;
  color: #94a3b8;
  font-size: 12px;
}

.blog-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.detail-section {
  margin-bottom: 22px;
}

.detail-section h4 {
  margin: 0 0 12px;
  font-size: 13px;
  color: #334155;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.detail-item {
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.detail-item span {
  display: block;
  color: #94a3b8;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  display: block;
  color: #334155;
  font-size: 12px;
  word-break: break-word;
}

.content-box {
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}

.content-box p {
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-line;
}

.blog-content {
  max-height: 250px;
  overflow-y: auto;
}

/* Delete */
.delete-modal {
  width: 400px;
  max-width: 100%;
  background: white;
  border-radius: 14px;
  padding: 28px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.delete-modal h2 {
  margin: 0 0 8px;
  font-size: 19px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.delete-actions {
  margin-top: 22px;
  display: flex;
  justify-content: center;
  gap: 9px;
}

/* Responsive */
@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .search-box {
    max-width: none;
    flex-basis: 100%;
  }
}

@media (max-width: 700px) {
  .blogs-wrapper {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    gap: 15px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    width: 100%;
  }

  .filter-select,
  .reset-btn {
    width: 100%;
  }

  .form-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>